/**
* Per-entity depth data for the /apostille/* cluster.
*
* Purpose: every destination-country page and every document-type page must
* carry facts that are true for THAT entity only (receiving authority,
* accepted translator system, language of the target copy, typical rejection
* causes). This is what keeps the cluster out of "templated doorway" territory
* and is the material answer engines quote.
*
* Reviewed: 2026-07-30 · Verify current practice with กรมการกงสุล
* (consular.mfa.go.th) and the receiving authority before filing.
*/

export type CountryDepth = {
  /** Named authorities in the destination that actually receive the document. */
  authorities: string[];
  /** Language the destination expects the translation in. */
  language: string;
  /** Translator status accepted at destination (system name in local wording). */
  translator: string;
  /** Whether anything beyond Apostille is commonly asked for. */
  beyondApostille: string;
  /** What Thai clients most often file there. */
  commonUses: string[];
  /** Document-age rule the destination commonly applies. */
  validityNote: string;
  /** Country-specific reasons documents get bounced. */
  pitfalls: string[];
};

const C = (d: CountryDepth) => d;

export const COUNTRY_DEPTH: Record<string, CountryDepth> = {
  usa: C({
    authorities: ["USCIS", "Social Security Administration", "County Clerk ของรัฐปลายทาง", "มหาวิทยาลัย/ WES"],
    language: "อังกฤษ",
    translator: "certified translation พร้อม Certificate of Accuracy ระบุชื่อ-ที่อยู่ผู้แปล (USCIS ไม่บังคับ notary)",
    beyondApostille:
      "USCIS รับคำแปลแบบ certified โดยไม่ต้อง notarize แต่หน่วยงานระดับรัฐ เช่น DMV หรือศาลครอบครัวบางรัฐ ขอให้ผู้แปลลงนามต่อหน้า notary ของรัฐนั้น",
    commonUses: ["ยื่นวีซ่าคู่สมรส I-130", "ปรับสถานะ Green Card", "เทียบวุฒิเข้ามหาวิทยาลัย", "ขอ SSN ให้บุตร"],
    validityNote: "USCIS มักขอสูติบัตร/ทะเบียนสมรสฉบับคัดใหม่ไม่เกิน 12 เดือน ส่วนใบรับรองโสดมักไม่เกิน 6 เดือน",
    pitfalls: [
      "ชื่อในคำแปลใช้การถอดเสียงคนละแบบกับพาสปอร์ต ทำให้ USCIS ออก RFE",
      "แปลเฉพาะหน้าแรก ไม่แปลตราประทับและข้อความหลังเอกสาร",
      "ใช้สำเนาถ่ายที่ไม่มีลายมือชื่อนายทะเบียน จึงรับ Apostille ไม่ได้",
    ],
  }),
  uk: C({
    authorities: ["UKVI / Home Office", "General Register Office", "UK NARIC (Ecctis)", "HM Courts"],
    language: "อังกฤษ",
    translator: "certified translation จากบริษัทแปล หรือสมาชิก ITI / CIOL พร้อมคำรับรองความถูกต้อง",
    beyondApostille: "เมื่อมี Apostille ไทยแล้ว ไม่ต้องนำไปรับรองที่สถานทูตอังกฤษอีก",
    commonUses: ["Spouse/Partner visa", "จดทะเบียนสมรสในอังกฤษ", "เทียบวุฒิผ่าน Ecctis", "ยื่นคดีครอบครัวในศาลอังกฤษ"],
    validityNote: "Home Office มักขอเอกสารทะเบียนราษฎรที่คัดใหม่ภายใน 6 เดือนก่อนวันยื่น",
    pitfalls: [
      "คำแปลไม่มีวันที่และข้อมูลติดต่อผู้แปล ทำให้ถือว่าไม่ใช่ certified translation",
      "ยื่นทะเบียนสมรสไทยโดยไม่แนบ คร.2 ที่ระบุรายละเอียดคู่สมรสครบ",
      "ทำ Apostille บนต้นฉบับแต่ไม่ได้รับรองคำแปลไปพร้อมกันเป็นชุดเดียว",
    ],
  }),
  australia: C({
    authorities: ["Department of Home Affairs", "VETASSESS / Engineers Australia", "AHPRA", "Births Deaths and Marriages ของรัฐ", "VicRoads"],
    language: "อังกฤษ",
    translator: "NAATI Certified Translator เท่านั้น (ต้องมีตรา NAATI, หมายเลขผู้แปล, วันหมดอายุ certification และลายเซ็น)",
    beyondApostille:
      "หน่วยงานออสเตรเลียส่วนใหญ่ตัดสินจากตรา NAATI เป็นหลัก การทำ Apostille เพิ่มใช้ในกรณีที่ปลายทางขอหลักฐานความแท้ของเอกสารต้นฉบับ",
    commonUses: ["Partner visa subclass 820/309", "Skills assessment", "ขึ้นทะเบียนวิชาชีพ", "โอนใบขับขี่"],
    validityNote: "Home Affairs มักขอ police clearance ที่ออกไม่เกิน 12 เดือน และใบรับรองโสดไม่เกิน 3-6 เดือน",
    pitfalls: [
      "ใช้คำแปลที่ไม่ใช่ NAATI จึงถูกขอเอกสารเพิ่มทั้งชุด",
      "ตรา NAATI ของผู้แปลหมดอายุ ณ วันที่ยื่น",
      "แปลชื่อหน่วยงานไทยคนละแบบในเอกสารแต่ละฉบับของชุดเดียวกัน",
    ],
  }),
  "new-zealand": C({
    authorities: ["Immigration New Zealand", "NZQA", "Registrar-General Births Deaths & Marriages"],
    language: "อังกฤษ",
    translator: "คำแปลจากผู้แปลมืออาชีพที่ไม่ใช่ผู้ยื่นเอง หรือ NAATI (INZ ยอมรับทั้งสองแนวทาง)",
    beyondApostille: "ไม่ต้องผ่านสถานทูตนิวซีแลนด์ซ้ำหลังมี Apostille",
    commonUses: ["Partnership visa", "เทียบวุฒิ NZQA", "จดทะเบียนสมรสในนิวซีแลนด์"],
    validityNote: "INZ มักขอเอกสารสถานะบุคคลที่คัดใหม่ไม่เกิน 6 เดือน",
    pitfalls: [
      "ให้ญาติหรือผู้ยื่นแปลเอง INZ ปฏิเสธเพราะไม่เป็นกลาง",
      "ส่งไฟล์สแกนที่อ่านตราประทับไม่ออก",
      "ไม่แปลหมายเลขทะเบียนเอกสารให้ตรงต้นฉบับ",
    ],
  }),
  japan: C({
    authorities: ["สำนักงานเขต/ศาลากลาง (市区町村役場)", "สำนักงานตรวจคนเข้าเมือง (入国管理局)", "มหาวิทยาลัยญี่ปุ่น"],
    language: "ญี่ปุ่น (บางหน่วยงานรับอังกฤษ)",
    translator: "ผู้แปลระบุชื่อและลงนามรับรอง (翻訳証明) — บางกรณีปลายทางขอ notarization ที่ 公証役場 ในญี่ปุ่น",
    beyondApostille: "หลังมี Apostille ไทยแล้วไม่ต้องผ่านสถานทูตญี่ปุ่น แต่ปลายทางมักขอคำแปลภาษาญี่ปุ่นแนบเพิ่ม",
    commonUses: ["จดทะเบียนสมรสที่ญี่ปุ่น (婚姻届)", "ขอวีซ่าคู่สมรส", "ยื่นสถานะพำนัก", "สมัครเรียนต่อ"],
    validityNote: "เขตในญี่ปุ่นมักขอใบรับรองโสดและสูติบัตรที่ออกไม่เกิน 3 เดือน",
    pitfalls: [
      "แปลเป็นอังกฤษอย่างเดียวแล้วเขตขอฉบับญี่ปุ่นเพิ่ม ทำให้เสียเวลารอบสอง",
      "ตัวคันจิของชื่อคู่สมรสไม่ตรงกับที่จดไว้ในทะเบียนญี่ปุ่น",
      "เอกสารเกิน 3 เดือนระหว่างรอคิวยื่นที่เขต",
    ],
  }),
  korea: C({
    authorities: ["สำนักงานเขต (구청/시청)", "Korea Immigration Service", "NIIED"],
    language: "เกาหลี",
    translator: "คำแปลเกาหลีพร้อมคำรับรองผู้แปล บางเขตขอ notarization ที่เกาหลี",
    beyondApostille: "ไม่ต้องผ่านสถานทูตเกาหลีหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "วีซ่า F-6", "ยื่นสถานะผู้พำนัก", "สมัครทุนการศึกษา"],
    validityNote: "เขตในเกาหลีมักกำหนดอายุเอกสารไม่เกิน 3 เดือน",
    pitfalls: [
      "การถอดชื่อไทยเป็นฮันกึลไม่ตรงกับที่ใช้ในระบบ Immigration",
      "ยื่นทะเบียนบ้านโดยไม่แปลหน้ารายการบุคคลทั้งหมด",
      "ใบรับรองโสดหมดอายุระหว่างรอคิว",
    ],
  }),
  germany: C({
    authorities: ["Standesamt", "Ausländerbehörde", "Zentralstelle für ausländisches Bildungswesen (anabin)"],
    language: "เยอรมัน",
    translator: "beeidigter/vereidigter Übersetzer (นักแปลสาบานตนที่ศาลเยอรมันแต่งตั้ง) — ผลลัพธ์เรียกว่า beglaubigte Übersetzung",
    beyondApostille: "Standesamt หลายแห่งส่งเอกสารเข้ากระบวนการ Befreiung จากศาล Oberlandesgericht ซึ่งใช้เวลาเพิ่มอีกหลายสัปดาห์",
    commonUses: ["จดทะเบียนสมรสในเยอรมนี", "วีซ่าติดตามคู่สมรส", "เทียบวุฒิ anabin", "ขอสัญชาติ"],
    validityNote: "Standesamt มักขอใบรับรองโสดและสูติบัตรที่ออกไม่เกิน 6 เดือน",
    pitfalls: [
      "ใช้คำแปลที่ทำในไทยโดยผู้แปลที่ไม่ได้สาบานตนในเยอรมนี",
      "ไม่เผื่อเวลาสำหรับขั้น Befreiung ของศาล ทำให้เอกสารหมดอายุก่อนได้คิว",
      "ชื่อสกุลหลังสมรสในเอกสารไทยไม่สอดคล้องกับพาสปอร์ต",
    ],
  }),
  france: C({
    authorities: ["Mairie (สำนักงานเทศบาล)", "Consulat / Préfecture", "Campus France"],
    language: "ฝรั่งเศส",
    translator: "traducteur assermenté ที่ขึ้นทะเบียนกับ Cour d'appel — ผลลัพธ์คือ traduction assermentée",
    beyondApostille: "ไม่ต้องผ่านสถานทูตฝรั่งเศสหลังมี Apostille แต่ Mairie จะยอมรับเฉพาะคำแปล assermentée",
    commonUses: ["จดทะเบียนสมรสที่ Mairie", "วีซ่าคู่สมรส", "สมัครเรียน Campus France", "ขอ titre de séjour"],
    validityNote: "Mairie มักขอสูติบัตรที่คัดใหม่ไม่เกิน 6 เดือน (3 เดือนสำหรับบางเขต)",
    pitfalls: [
      "ใช้ certified translation ทั่วไปแทน traducteur assermenté",
      "ยื่นเอกสารโดยไม่มี Apostille บนคำแปล ทำให้ Mairie ตีกลับทั้งชุด",
      "แปลชื่อสถานที่เกิดไม่ตรงกับที่ระบุในพาสปอร์ต",
    ],
  }),
  italy: C({
    authorities: ["Comune", "Questura", "มหาวิทยาลัยอิตาลี"],
    language: "อิตาลี",
    translator: "traduzione giurata ผ่านกระบวนการ asseverazione ที่ศาลอิตาลี หรือคำแปลที่สถานทูตอิตาลีรับรอง",
    beyondApostille: "Comune รับ Apostille แต่ยืนกรานให้คำแปลผ่าน asseverazione",
    commonUses: ["จดทะเบียนสมรส", "ขอ permesso di soggiorno", "เทียบวุฒิ dichiarazione di valore"],
    validityNote: "Comune มักขอเอกสารทะเบียนราษฎรอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "แปลในไทยแล้วไม่ได้ผ่าน asseverazione ที่อิตาลี",
      "ขาด dichiarazione di valore สำหรับเอกสารการศึกษา",
      "ตราประทับ Apostille ถูกแกะออกจากชุดเอกสารระหว่างถ่ายสำเนา",
    ],
  }),
  spain: C({
    authorities: ["Registro Civil", "Oficina de Extranjería", "มหาวิทยาลัยสเปน"],
    language: "สเปน",
    translator: "traductor jurado ที่ขึ้นทะเบียนกับ MAEC — ผลลัพธ์คือ traducción jurada",
    beyondApostille: "ไม่ต้องผ่านสถานทูตสเปนหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "ขอ NIE / residencia", "homologación วุฒิการศึกษา"],
    validityNote: "Registro Civil มักขอเอกสารอายุไม่เกิน 3-6 เดือน",
    pitfalls: [
      "ใช้ผู้แปลที่ไม่ได้ขึ้นทะเบียน MAEC",
      "ไม่แปลตรา Apostille ซึ่งบางสำนักงานขอให้แปลด้วย",
      "ยื่นสำเนาสีแทนชุดต้นฉบับที่มีตราจริง",
    ],
  }),
  netherlands: C({
    authorities: ["Gemeente", "IND", "Nuffic"],
    language: "ดัตช์หรืออังกฤษ",
    translator: "beëdigd vertaler ที่ขึ้นทะเบียน Rbtv (สำหรับคำแปลดัตช์) หรือคำแปลอังกฤษที่ Gemeente รับ",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["ลงทะเบียน BRP", "MVV / วีซ่าคู่ชีวิต", "เทียบวุฒิ Nuffic"],
    validityNote: "Gemeente มักขอเอกสารทะเบียนราษฎรที่ออกไม่เกิน 6 เดือน",
    pitfalls: [
      "ยื่นคำแปลอังกฤษกับ Gemeente ที่รับเฉพาะดัตช์",
      "ชื่อสะกดใน BRP ไม่ตรงกับเอกสารไทย",
      "ไม่ได้ทำ Apostille ครอบคลุมคำแปล",
    ],
  }),
  switzerland: C({
    authorities: ["Zivilstandsamt ของ Kanton", "Migrationsamt", "swissuniversities"],
    language: "เยอรมัน ฝรั่งเศส หรืออิตาลี ตาม Kanton",
    translator: "นักแปลสาบานตนที่ Kanton ปลายทางยอมรับ",
    beyondApostille: "ข้อกำหนดต่างกันตาม Kanton จึงควรขอ checklist จาก Zivilstandsamt ก่อนเริ่ม",
    commonUses: ["จดทะเบียนสมรส", "ขอใบอนุญาตพำนัก", "รวมครอบครัว"],
    validityNote: "Kanton ส่วนใหญ่กำหนดอายุเอกสาร 3-6 เดือน",
    pitfalls: [
      "ใช้ข้อกำหนดของ Kanton หนึ่งกับอีก Kanton หนึ่ง",
      "แปลผิดภาษาราชการของ Kanton ปลายทาง",
      "เอกสารหมดอายุระหว่างรอคิวสัมภาษณ์",
    ],
  }),
  sweden: C({
    authorities: ["Skatteverket", "Migrationsverket", "UHR"],
    language: "สวีเดนหรืออังกฤษ",
    translator: "auktoriserad translator (Kammarkollegiet) หรือคำแปลอังกฤษที่รับรองแล้ว",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["ลงทะเบียน personnummer", "วีซ่าติดตามคู่สมรส", "เทียบวุฒิ UHR"],
    validityNote: "Skatteverket มักขอเอกสารสถานะบุคคลไม่เกิน 6 เดือน",
    pitfalls: [
      "ไม่แปลหน้าที่มีตราประทับด้านหลัง",
      "ยื่นทะเบียนสมรสโดยไม่มีใบรับรองโสดของอีกฝ่าย",
      "ข้อมูลวันเดือนปีสลับรูปแบบ (พ.ศ./ค.ศ.)",
    ],
  }),
  norway: C({
    authorities: ["UDI", "Skatteetaten", "NOKUT"],
    language: "นอร์เวย์หรืออังกฤษ",
    translator: "statsautorisert translatør หรือคำแปลอังกฤษรับรอง",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["family immigration", "ลงทะเบียนที่อยู่", "เทียบวุฒิ NOKUT"],
    validityNote: "UDI มักขอเอกสารที่ออกไม่เกิน 6 เดือน",
    pitfalls: [
      "แปลปีเป็น พ.ศ. โดยไม่แปลงเป็น ค.ศ.",
      "ไม่แนบสำเนาพาสปอร์ตที่สะกดชื่อตรงกับคำแปล",
      "ยื่นเอกสารเป็นไฟล์ภาพความละเอียดต่ำ",
    ],
  }),
  denmark: C({
    authorities: ["Familieretshuset", "SIRI", "Borgerservice"],
    language: "เดนมาร์กหรืออังกฤษ",
    translator: "คำแปลรับรองจากบริษัทแปลหรือนักแปลที่ระบุตัวตนได้",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรสในเดนมาร์ก", "family reunification", "ขอ CPR"],
    validityNote: "Familieretshuset มักขอใบรับรองโสดที่ออกไม่เกิน 4 เดือน",
    pitfalls: [
      "ยื่นเอกสารสถานะโสดที่เกินกำหนดอายุ",
      "ไม่ได้ทำ Apostille ทั้งต้นฉบับและคำแปล",
      "รายละเอียดที่อยู่ในทะเบียนบ้านไม่ตรงกับที่แจ้งในคำร้อง",
    ],
  }),
  finland: C({
    authorities: ["DVV (Digital and Population Data Services Agency)", "Migri", "Opetushallitus"],
    language: "ฟินแลนด์ สวีเดน หรืออังกฤษ",
    translator: "auktorisoitu kääntäjä หรือคำแปลอังกฤษรับรอง",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["ลงทะเบียนประชากร", "residence permit", "เทียบวุฒิ"],
    validityNote: "DVV มักขอเอกสารทะเบียนราษฎรอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ไม่แปลหมายเลขประจำตัวประชาชนให้ตรงรูปแบบ",
      "ยื่นสำเนาโดยไม่มีตรารับรองสำเนาถูกต้อง",
      "ชื่อกลางหายไปจากคำแปล",
    ],
  }),
  poland: C({
    authorities: ["Urząd Stanu Cywilnego (USC)", "Urząd Wojewódzki", "NAWA"],
    language: "โปแลนด์",
    translator: "tłumacz przysięgły (นักแปลสาบานตนขึ้นทะเบียนกระทรวงยุติธรรมโปแลนด์)",
    beyondApostille: "USC ยอมรับ Apostille แต่ยืนกรานคำแปลจาก tłumacz przysięgły",
    commonUses: ["จดทะเบียนสมรส", "karta pobytu", "เทียบวุฒิ"],
    validityNote: "USC มักขอเอกสารอายุไม่เกิน 3-6 เดือน",
    pitfalls: [
      "ใช้คำแปลอังกฤษยื่นกับ USC",
      "ไม่แปลตรา Apostille ตามที่ USC ขอ",
      "ชื่อเมืองเกิดสะกดต่างจากพาสปอร์ต",
    ],
  }),
  czech: C({
    authorities: ["Matrika (สำนักทะเบียน)", "Ministerstvo vnitra", "มหาวิทยาลัยเช็ก"],
    language: "เช็ก",
    translator: "soudní tlumočník (นักแปลที่ศาลแต่งตั้ง)",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "ขอถิ่นที่อยู่", "nostrifikace วุฒิการศึกษา"],
    validityNote: "Matrika มักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ใช้คำแปลที่ไม่ได้ทำโดย soudní tlumočník",
      "ไม่แนบต้นฉบับที่มี Apostille ติดกับคำแปล",
      "ยื่นเอกสารการศึกษาโดยไม่ผ่าน nostrifikace",
    ],
  }),
  greece: C({
    authorities: ["Ληξιαρχείο (สำนักทะเบียน)", "Αποκεντρωμένη Διοίκηση", "ΔΟΑΤΑΠ"],
    language: "กรีก",
    translator: "คำแปลจากทนายความกรีกที่มีสิทธิรับรองคำแปล หรือหน่วยแปลของกระทรวงการต่างประเทศกรีซ",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "ขอถิ่นที่อยู่", "เทียบวุฒิ ΔΟΑΤΑΠ"],
    validityNote: "สำนักทะเบียนมักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ใช้คำแปลอังกฤษกับสำนักทะเบียนที่รับเฉพาะกรีก",
      "การถอดชื่อเป็นอักษรกรีกไม่ตรงกับที่เคยใช้",
      "ตราประทับบนคำแปลไม่ชัด",
    ],
  }),
  ireland: C({
    authorities: ["General Register Office (GRO)", "INIS / Department of Justice", "QQI"],
    language: "อังกฤษ",
    translator: "certified translation พร้อมคำรับรองของผู้แปล",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["แจ้งความจำนงสมรส 3 เดือน", "join family visa", "เทียบวุฒิ QQI"],
    validityNote: "GRO มักขอเอกสารสถานะโสดอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ยื่นแจ้งความจำนงสมรสช้ากว่ากำหนด 3 เดือน",
      "คำแปลไม่มีข้อมูลติดต่อผู้แปล",
      "เอกสารมี Apostille แต่คำแปลทำหลังจากนั้นและไม่ได้รับรอง",
    ],
  }),
  portugal: C({
    authorities: ["Conservatória do Registo Civil", "AIMA (เดิม SEF)", "DGES"],
    language: "โปรตุเกส",
    translator: "tradução certificada รับรองโดยทนาย/notário หรือสถานกงสุลโปรตุเกส",
    beyondApostille: "ไม่ต้องผ่านสถานทูตหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "ขอถิ่นที่อยู่", "reconhecimento วุฒิการศึกษา"],
    validityNote: "Conservatória มักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ยื่นคำแปลอังกฤษแทนโปรตุเกส",
      "ไม่รับรองคำแปลโดย notário",
      "ข้อมูลบิดามารดาในสูติบัตรแปลไม่ครบ",
    ],
  }),
  china: C({
    authorities: ["สำนักงานกิจการพลเรือน (民政局)", "สำนักงานตรวจคนเข้าเมือง", "มหาวิทยาลัยจีน", "CSCSE"],
    language: "จีนตัวย่อ",
    translator: "หน่วยงานแปลที่จดทะเบียนในจีน หรือคำแปลที่ผ่าน 公证处 (notary public office)",
    beyondApostille:
      "จีนเข้าเป็นภาคีอนุสัญญาตั้งแต่ปี 2023 จึงรับ Apostille แทนการรับรองสถานทูตได้ แต่บางหน่วยงานท้องถิ่นยังขอคำแปลที่ผ่าน 公证处 ในจีน",
    commonUses: ["จดทะเบียนสมรส", "work permit", "เรียนต่อ/เทียบวุฒิ", "ธุรกรรมบริษัท"],
    validityNote: "หน่วยงานจีนมักขอเอกสารสถานะบุคคลอายุไม่เกิน 3-6 เดือน",
    pitfalls: [
      "ใช้คำแปลจีนตัวเต็มกับหน่วยงานที่รับเฉพาะตัวย่อ",
      "หน่วยงานท้องถิ่นยังขอ 公证 เพิ่มแม้มี Apostille แล้ว",
      "การถอดชื่อไทยเป็นอักษรจีนไม่ตรงกับที่เคยใช้ในวีซ่าเดิม",
    ],
  }),
  india: C({
    authorities: ["Ministry of External Affairs (MEA)", "FRRO", "AIU"],
    language: "อังกฤษ",
    translator: "certified translation ภาษาอังกฤษ",
    beyondApostille: "MEA รับ Apostille ไทยโดยตรง ไม่ต้องผ่านสถานทูตอินเดียอีก",
    commonUses: ["วีซ่าคู่สมรส", "ลงทะเบียน FRRO", "เทียบวุฒิ AIU", "ธุรกรรมบริษัท"],
    validityNote: "FRRO มักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ชื่อบิดาในเอกสารไทยไม่ตรงกับที่ระบบอินเดียบันทึก",
      "ไม่แปลตราประทับด้านหลังเอกสาร",
      "ยื่นสำเนาที่ไม่มีลายมือชื่อเจ้าหน้าที่ผู้คัด",
    ],
  }),
  singapore: C({
    authorities: ["Singapore Academy of Law (SAL)", "ICA", "MOM", "SSG"],
    language: "อังกฤษ",
    translator: "certified translation ภาษาอังกฤษ",
    beyondApostille: "SAL เป็นหน่วยงาน Apostille ของสิงคโปร์ และรับ Apostille ไทยโดยตรง",
    commonUses: ["Employment Pass", "จดทะเบียนสมรส", "ขึ้นทะเบียนบริษัท", "สมัครเรียน"],
    validityNote: "ICA มักขอเอกสารสถานะบุคคลที่คัดใหม่ไม่เกิน 6 เดือน",
    pitfalls: [
      "ยื่นสำเนาสีแทนต้นฉบับที่มีตรา Apostille",
      "เอกสารบริษัทไม่ครบชุด (ขาดบัญชีรายชื่อผู้ถือหุ้น)",
      "คำแปลไม่ระบุคุณสมบัติผู้แปล",
    ],
  }),
  philippines: C({
    authorities: ["PSA", "DFA Office of Consular Affairs", "Bureau of Immigration", "CHED"],
    language: "อังกฤษ",
    translator: "certified translation ภาษาอังกฤษ",
    beyondApostille: "DFA ฟิลิปปินส์รับ Apostille ไทย ไม่ต้องผ่านสถานทูตอีก",
    commonUses: ["จดทะเบียนสมรส", "รายงานการเกิดของบุตร", "ขอวีซ่าติดตาม", "เทียบวุฒิ"],
    validityNote: "หน่วยงานฟิลิปปินส์มักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ชื่อในเอกสารไทยไม่มีนามสกุลกลางตามรูปแบบฟิลิปปินส์",
      "ไม่แนบใบรับรองโสดของฝ่ายไทย",
      "คำแปลใช้ตัวสะกดต่างจากพาสปอร์ต",
    ],
  }),
  russia: C({
    authorities: ["ЗАГС (สำนักทะเบียน)", "МВД (กรมกิจการภายใน)", "Рособрнадзор"],
    language: "รัสเซีย",
    translator: "คำแปลรัสเซียที่ผ่าน нотариус (notary) ในรัสเซีย",
    beyondApostille: "ไม่ต้องผ่านสถานทูตรัสเซียหลังมี Apostille แต่คำแปลต้องรับรองที่รัสเซีย",
    commonUses: ["จดทะเบียนสมรส", "ขอถิ่นที่อยู่", "เทียบวุฒิ"],
    validityNote: "ЗАГС มักขอเอกสารอายุไม่เกิน 3-6 เดือน",
    pitfalls: [
      "แปลในไทยแล้วไม่ได้รับรองโดย нотариус",
      "การถอดชื่อเป็นอักษรซีริลลิกไม่ตรงกับวีซ่า",
      "ไม่แปลตรา Apostille",
    ],
  }),
  brazil: C({
    authorities: ["Cartório de Registro Civil", "Polícia Federal", "MEC"],
    language: "โปรตุเกส",
    translator: "tradutor público juramentado ที่ขึ้นทะเบียนกับ Junta Comercial",
    beyondApostille: "ไม่ต้องผ่านสถานทูตบราซิลหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "ขอ CRNM", "revalidação วุฒิการศึกษา"],
    validityNote: "Cartório มักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "ใช้คำแปลที่ไม่ใช่ tradutor juramentado",
      "ไม่ทำ registro ของคำแปลที่ Cartório",
      "ข้อมูลบิดามารดาไม่ครบในสูติบัตรแปล",
    ],
  }),
  mexico: C({
    authorities: ["Registro Civil", "Instituto Nacional de Migración", "SEP"],
    language: "สเปน",
    translator: "perito traductor ที่ขึ้นทะเบียนกับศาลของรัฐปลายทาง",
    beyondApostille: "ไม่ต้องผ่านสถานทูตเม็กซิโกหลังมี Apostille",
    commonUses: ["จดทะเบียนสมรส", "residencia temporal", "revalidación วุฒิ"],
    validityNote: "Registro Civil มักขอเอกสารอายุไม่เกิน 3-6 เดือน",
    pitfalls: [
      "ใช้ผู้แปลที่ไม่ได้ขึ้นทะเบียนในรัฐที่ยื่น",
      "ไม่แปลตรา Apostille",
      "ชื่อสองนามสกุลตามระบบเม็กซิโกไม่สอดคล้องกับเอกสารไทย",
    ],
  }),
  "south-africa": C({
    authorities: ["Department of Home Affairs (DHA)", "SAQA", "DIRCO"],
    language: "อังกฤษ",
    translator: "sworn translator ที่ศาลแอฟริกาใต้รับรอง หรือคำแปลอังกฤษรับรอง",
    beyondApostille: "DHA รับ Apostille ไทยโดยตรง",
    commonUses: ["spousal visa", "จดทะเบียนสมรส", "เทียบวุฒิ SAQA"],
    validityNote: "DHA มักขอ police clearance และใบรับรองโสดที่ออกไม่เกิน 6 เดือน",
    pitfalls: [
      "ยื่นใบตรวจประวัติที่เกิน 6 เดือน",
      "คำแปลไม่ได้ทำโดย sworn translator ตามที่ DHA ขอ",
      "ชุดเอกสารถูกแยกออกจากกัน ตราประทับไม่ครบชุด",
    ],
  }),
  turkey: C({
    authorities: ["Nüfus Müdürlüğü", "Göç İdaresi", "YÖK"],
    language: "ตุรกี",
    translator: "yeminli tercüman พร้อม noter tasdiki (รับรองโดย notary ตุรกี)",
    beyondApostille: "ไม่ต้องผ่านสถานทูตตุรกีหลังมี Apostille แต่คำแปลต้องผ่าน noter ในตุรกี",
    commonUses: ["จดทะเบียนสมรส", "ikamet izni", "denklik วุฒิการศึกษา"],
    validityNote: "Nüfus Müdürlüğü มักขอเอกสารอายุไม่เกิน 6 เดือน",
    pitfalls: [
      "แปลในไทยแล้วไม่ผ่าน noter ตุรกี",
      "ไม่แปลตรา Apostille ตามที่ noter ขอ",
      "ชื่อถอดอักษรตุรกีไม่ตรงกับพาสปอร์ต",
    ],
  }),
};

export type DocDepth = {
  /** Thai authority that issues / re-issues the document. */
  issuer: string;
  /** How to obtain a fresh certified copy. */
  howToObtain: string;
  /** What must be true before MFA will legalise it. */
  prerequisites: string[];
  /** Age rule the document usually faces. */
  validity: string;
  /** Typical purposes abroad. */
  uses: string[];
  /** Translation notes specific to this document. */
  translationNote: string;
  /** Document-specific rejection causes. */
  pitfalls: string[];
};

const D = (d: DocDepth) => d;

export const DOC_DEPTH: Record<string, DocDepth> = {
  "birth-certificate": D({
    issuer: "สำนักงานเขต/ที่ว่าการอำเภอที่แจ้งเกิด (ทะเบียนราษฎร)",
    howToObtain: "ขอคัดสำเนาสูติบัตร (ท.ร.1/ท.ร.19) พร้อมตรารับรองสำเนาถูกต้องและลายมือชื่อนายทะเบียน ที่เขต/อำเภอใดก็ได้ที่เชื่อมระบบทะเบียนกลาง",
    prerequisites: [
      "เป็นฉบับที่มีลายมือชื่อและตราประทับนายทะเบียนของจริง ไม่ใช่สำเนาถ่าย",
      "ข้อมูลชื่อ-สกุล วันเกิด และสถานที่เกิด ตรงกับพาสปอร์ตปัจจุบัน",
      "หากเคยเปลี่ยนชื่อ ต้องแนบใบเปลี่ยนชื่อ-สกุลเป็นชุดเดียวกัน",
    ],
    validity: "ตัวเอกสารไม่มีวันหมดอายุ แต่ปลายทางส่วนใหญ่ขอฉบับคัดใหม่ภายใน 3-6 เดือน",
    uses: ["ยื่นวีซ่าติดตามครอบครัว", "แจ้งเกิดบุตรที่ต่างประเทศ", "สมัครเรียน", "ขอสัญชาติตามบิดามารดา"],
    translationNote: "ต้องแปลรายการบิดามารดา สถานที่เกิด และตราประทับด้านหลังให้ครบทุกช่อง มิฉะนั้นปลายทางถือว่าคำแปลไม่สมบูรณ์",
    pitfalls: [
      "ใช้สูติบัตรฉบับเก่าที่หมึกเลือนจนกรมการกงสุลตรวจลายมือชื่อไม่ได้",
      "สะกดชื่อสถานที่เกิดในคำแปลต่างจากที่ปรากฏในพาสปอร์ต",
      "ไม่แนบใบเปลี่ยนชื่อทำให้ชื่อในสูติบัตรกับพาสปอร์ตไม่ตรงกัน",
    ],
  }),
  "marriage-certificate": D({
    issuer: "สำนักทะเบียนอำเภอ/เขตที่จดทะเบียนสมรส",
    howToObtain: "คัดใบสำคัญการสมรส (คร.3) และทะเบียนสมรส (คร.2) ฉบับรับรองจากสำนักทะเบียนที่จด",
    prerequisites: [
      "ควรคัดทั้ง คร.2 และ คร.3 เพราะปลายทางหลายแห่งขอรายละเอียดใน คร.2",
      "ตรวจว่าชื่อคู่สมรสทั้งสองฝ่ายสะกดตรงกับพาสปอร์ตของแต่ละคน",
      "หากมีการเปลี่ยนนามสกุลหลังสมรส ต้องมีเอกสารรองรับ",
    ],
    validity: "ปลายทางมักขอฉบับคัดใหม่ภายใน 3-6 เดือน",
    uses: ["วีซ่าคู่สมรส", "ลงทะเบียนสถานะสมรสในต่างประเทศ", "สิทธิประโยชน์คู่สมรส", "คดีครอบครัว"],
    translationNote: "ต้องแปลทั้ง คร.2 และ คร.3 เป็นชุดเดียวกัน และคงเลขทะเบียนสมรสให้ตรงต้นฉบับทุกหลัก",
    pitfalls: [
      "แปลเฉพาะ คร.3 แล้วปลายทางขอ คร.2 เพิ่ม",
      "ชื่อคู่สมรสชาวต่างชาติในทะเบียนไทยสะกดต่างจากพาสปอร์ตของเขา",
      "เอกสารเกินอายุระหว่างรอคิวสถานทูต",
    ],
  }),
  "single-status": D({
    issuer: "สำนักทะเบียนอำเภอ/เขต หรือกรมการปกครอง",
    howToObtain: "ยื่นคำร้องขอหนังสือรับรองความเป็นโสด ณ อำเภอ/เขตตามทะเบียนบ้าน โดยต้องมีพยานรับรองตามที่นายทะเบียนกำหนด",
    prerequisites: [
      "ผู้ร้องต้องมีชื่อในทะเบียนบ้านไทยที่เป็นปัจจุบัน",
      "ถ้าเคยสมรสและหย่า ต้องแนบใบสำคัญการหย่า (คร.7)",
      "บางอำเภอกำหนดให้พยาน 2 คนไปให้ถ้อยคำด้วยตนเอง",
    ],
    validity: "เป็นเอกสารที่ปลายทางกำหนดอายุเข้มที่สุด โดยทั่วไป 3-6 เดือน บางประเทศ 3 เดือน",
    uses: ["จดทะเบียนสมรสในต่างประเทศ", "แจ้งความจำนงสมรส", "วีซ่าคู่หมั้น"],
    translationNote: "ต้องแปลข้อความรับรองของนายทะเบียนแบบคำต่อคำ และแปลชื่อพยานให้ครบตามต้นฉบับ",
    pitfalls: [
      "ขอเอกสารเร็วเกินไปจนหมดอายุก่อนถึงวันจดทะเบียนที่ปลายทาง",
      "ไม่แนบใบหย่าทำให้ปลายทางสงสัยสถานะ",
      "ที่อยู่ในทะเบียนบ้านไม่เป็นปัจจุบัน นายทะเบียนจึงไม่ออกให้",
    ],
  }),
  divorce: D({
    issuer: "สำนักทะเบียนอำเภอ/เขต (กรณีหย่าโดยความยินยอม) หรือศาล (กรณีหย่าโดยคำพิพากษา)",
    howToObtain: "คัดใบสำคัญการหย่า (คร.7) และทะเบียนการหย่า (คร.6) หรือขอคัดคำพิพากษาพร้อมหนังสือรับรองคดีถึงที่สุดจากศาล",
    prerequisites: [
      "กรณีหย่าตามคำพิพากษาต้องมีหนังสือรับรองคดีถึงที่สุด",
      "ต้องมีตราประทับและลายมือชื่อเจ้าหน้าที่ผู้คัด",
      "ตรวจว่าชื่อคู่หย่าตรงกับเอกสารแสดงตนปัจจุบัน",
    ],
    validity: "ปลายทางมักขอฉบับคัดใหม่ภายใน 3-6 เดือน",
    uses: ["จดทะเบียนสมรสใหม่", "ปรับสถานะในทะเบียนราษฎรต่างประเทศ", "คดีแบ่งทรัพย์สิน", "สิทธิเลี้ยงดูบุตร"],
    translationNote: "ต้องแปลเงื่อนไขการปกครองบุตรและข้อตกลงทรัพย์สินให้ครบ เพราะปลายทางใช้ข้อความส่วนนี้ตัดสินสิทธิ",
    pitfalls: [
      "แปลเฉพาะ คร.7 โดยไม่แปล คร.6 ที่มีข้อตกลงแนบท้าย",
      "คำพิพากษาไม่มีหนังสือรับรองคดีถึงที่สุด",
      "ข้อความข้อตกลงถูกสรุปย่อแทนการแปลคำต่อคำ",
    ],
  }),
  "house-registration": D({
    issuer: "สำนักงานเขต/ที่ว่าการอำเภอ (ทะเบียนราษฎร)",
    howToObtain: "ขอคัดและรับรองสำเนาทะเบียนบ้าน (ท.ร.14) ฉบับเจ้าหน้าที่ลงนาม",
    prerequisites: [
      "ต้องเป็นฉบับที่เจ้าหน้าที่ทะเบียนลงนามรับรอง ไม่ใช่เล่มถ่ายเอกสาร",
      "รายการบุคคลในบ้านต้องเป็นปัจจุบัน",
      "ถ้าย้ายที่อยู่แล้ว ควรคัดฉบับใหม่หลังย้าย",
    ],
    validity: "ปลายทางมักขอฉบับคัดใหม่ภายใน 3-6 เดือน",
    uses: ["พิสูจน์ที่อยู่", "ยื่นวีซ่า", "พิสูจน์ความสัมพันธ์ในครอบครัว", "ธุรกรรมทรัพย์สิน"],
    translationNote: "ต้องแปลทั้งหน้ารายการบ้านและหน้ารายการบุคคลทุกคนที่เกี่ยวข้อง ไม่ใช่เฉพาะหน้าของผู้ยื่น",
    pitfalls: [
      "แปลเฉพาะหน้าแรกจนขาดรายการบุคคลที่ปลายทางต้องใช้พิสูจน์ความสัมพันธ์",
      "ใช้สำเนาเล่มสีน้ำเงินที่ไม่มีลายมือชื่อเจ้าหน้าที่",
      "เลขรหัสประจำบ้านในคำแปลคลาดจากต้นฉบับ",
    ],
  }),
  "name-change": D({
    issuer: "สำนักทะเบียนอำเภอ/เขต",
    howToObtain: "คัดหนังสือสำคัญการเปลี่ยนชื่อตัว (ช.3) หรือเปลี่ยนชื่อสกุล (ช.5/ช.2) ฉบับรับรอง",
    prerequisites: [
      "ต้องมีเอกสารครบทุกครั้งที่เปลี่ยน หากเปลี่ยนหลายครั้งต้องยื่นทั้งชุดเพื่อเชื่อมชื่อเดิมถึงชื่อปัจจุบัน",
      "ตราประทับและลายมือชื่อนายทะเบียนต้องชัดเจน",
    ],
    validity: "ไม่มีวันหมดอายุ แต่ปลายทางมักขอฉบับคัดใหม่ภายใน 6 เดือน",
    uses: ["เชื่อมชื่อเดิม-ชื่อใหม่ในเอกสารต่างประเทศ", "แก้ไขชื่อในทะเบียนสมรสต่างประเทศ", "เทียบวุฒิที่ออกในชื่อเดิม"],
    translationNote: "คำแปลต้องแสดงทั้งชื่อเดิมและชื่อใหม่ในบรรทัดเดียวกับต้นฉบับ เพื่อให้ปลายทางเห็นสายโซ่ของชื่อ",
    pitfalls: [
      "ยื่นเฉพาะใบล่าสุด ทำให้ชื่อในปริญญาบัตรเก่าเชื่อมกับพาสปอร์ตไม่ได้",
      "ถอดเสียงชื่อใหม่ต่างจากที่กรมการกงสุลเคยรับรองไว้ก่อนหน้า",
      "ใช้สำเนาที่ไม่มีตราครุฑ",
    ],
  }),
  "police-clearance": D({
    issuer: "กองบัญชาการตำรวจสันติบาล สำนักงานตำรวจแห่งชาติ",
    howToObtain: "ยื่นคำร้องพร้อมพิมพ์ลายนิ้วมือที่ศูนย์บริการออกหนังสือรับรองความประพฤติ (ปทุมวัน) หรือผ่านสถานทูตไทยหากอยู่ต่างประเทศ",
    prerequisites: [
      "ต้องมีลายพิมพ์นิ้วมือที่ได้มาตรฐานและสำเนาพาสปอร์ต",
      "ต้องระบุประเทศและวัตถุประสงค์ปลายทางในคำร้อง",
      "ระยะเวลาออกเอกสารโดยทั่วไป 15-30 วันทำการ ต้องเผื่อไว้ในแผนงาน",
    ],
    validity: "ปลายทางส่วนใหญ่รับเฉพาะฉบับที่ออกไม่เกิน 3-12 เดือน (ออสเตรเลียและแอฟริกาใต้มักกำหนด 12 เดือน)",
    uses: ["วีซ่าถาวร", "ขึ้นทะเบียนวิชาชีพ", "สมัครงานต่างประเทศ", "ขอสัญชาติ"],
    translationNote: "หนังสือรับรองความประพฤติออกเป็นภาษาอังกฤษอยู่แล้วในหลายกรณี หากออกเป็นไทยต้องแปลข้อความรับรองและตราสันติบาลให้ครบ",
    pitfalls: [
      "ขอเอกสารล่วงหน้านานเกินไปจนหมดอายุก่อนวันตัดสินคำร้อง",
      "ระบุประเทศปลายทางผิด ทำให้ต้องยื่นใหม่ทั้งกระบวนการ",
      "ลายพิมพ์นิ้วมือไม่ชัด ถูกตีกลับตั้งแต่ขั้นออกเอกสาร",
    ],
  }),
  degree: D({
    issuer: "สถาบันการศึกษาที่ประสาทปริญญา",
    howToObtain: "ขอหนังสือรับรองการสำเร็จการศึกษาหรือสำเนาปริญญาบัตรที่นายทะเบียนของสถาบันลงนามรับรอง",
    prerequisites: [
      "ต้องมีลายมือชื่อนายทะเบียนหรือผู้มีอำนาจของสถาบัน ไม่ใช่สำเนาถ่าย",
      "ชื่อในปริญญาบัตรต้องเชื่อมกับชื่อปัจจุบันได้ (แนบใบเปลี่ยนชื่อถ้ามี)",
      "สถาบันเอกชนบางแห่งต้องผ่านการรับรองจากหน่วยงานต้นสังกัดก่อน",
    ],
    validity: "ไม่มีวันหมดอายุ แต่หน่วยงานเทียบวุฒิมักขอฉบับรับรองใหม่ภายใน 6-12 เดือน",
    uses: ["เทียบวุฒิ", "สมัครงานต่างประเทศ", "ขอวีซ่าทักษะ", "สมัครเรียนต่อ"],
    translationNote: "ต้องแปลชื่อสถาบันและชื่อปริญญาให้ตรงกับชื่อทางการภาษาอังกฤษที่สถาบันใช้ ไม่ควรแปลเอง",
    pitfalls: [
      "แปลชื่อคณะ/สาขาไม่ตรงกับที่สถาบันใช้ในระบบสากล",
      "ยื่นปริญญาบัตรโดยไม่มีทรานสคริปต์ประกอบ",
      "ใช้สำเนาที่ไม่มีลายมือชื่อนายทะเบียน",
    ],
  }),
  transcript: D({
    issuer: "สำนักทะเบียนของสถาบันการศึกษา",
    howToObtain: "ขอทรานสคริปต์ฉบับภาษาอังกฤษที่ปิดผนึกและมีลายมือชื่อนายทะเบียน",
    prerequisites: [
      "ควรขอฉบับภาษาอังกฤษโดยตรงจากสถาบันเพื่อลดขั้นตอนแปล",
      "ต้องมีตราสถาบันและลายมือชื่อนายทะเบียนบนทุกแผ่น",
      "หน่วยงานเทียบวุฒิบางแห่งขอให้สถาบันส่งตรง",
    ],
    validity: "ไม่มีวันหมดอายุ แต่มักขอฉบับออกใหม่ภายใน 6-12 เดือน",
    uses: ["เทียบวุฒิ", "สมัครเรียนต่อ", "ขึ้นทะเบียนวิชาชีพ"],
    translationNote: "ถ้าสถาบันออกเป็นไทย ต้องแปลรายวิชา หน่วยกิต และเกณฑ์เกรดทั้งหมด รวมถึงคำอธิบายระบบเกรดด้านหลัง",
    pitfalls: [
      "ไม่แปลตารางคำอธิบายระบบเกรด ทำให้หน่วยงานเทียบวุฒิคำนวณ GPA ไม่ได้",
      "แปลชื่อรายวิชาแบบย่อจนไม่ตรงกับคำอธิบายหลักสูตร",
      "เปิดซองที่ปิดผนึกก่อนยื่น",
    ],
  }),
  "company-affidavit": D({
    issuer: "กรมพัฒนาธุรกิจการค้า (DBD) กระทรวงพาณิชย์",
    howToObtain: "ขอคัดหนังสือรับรองนิติบุคคลฉบับภาษาอังกฤษพร้อมตรานายทะเบียน ที่ DBD หรือผ่านระบบ e-Service",
    prerequisites: [
      "หนังสือรับรองต้องออกไม่เกิน 3-6 เดือนสำหรับธุรกรรมส่วนใหญ่",
      "ควรคัด บอจ.5 (บัญชีรายชื่อผู้ถือหุ้น) และวัตถุประสงค์แนบไปด้วย",
      "ผู้มีอำนาจลงนามต้องเป็นปัจจุบัน",
    ],
    validity: "ปลายทางเชิงธุรกิจมักรับเฉพาะฉบับที่ออกไม่เกิน 3 เดือน",
    uses: ["เปิดบริษัทลูกในต่างประเทศ", "เปิดบัญชีธนาคารต่างประเทศ", "ประมูลงาน", "จดเครื่องหมายการค้า"],
    translationNote: "หาก DBD ออกฉบับภาษาอังกฤษให้ได้ ควรใช้ฉบับนั้นแทนการแปล เพราะกรมการกงสุลรับรองลายมือชื่อนายทะเบียนได้โดยตรง",
    pitfalls: [
      "ใช้หนังสือรับรองเก่าที่กรรมการเปลี่ยนไปแล้ว",
      "ขาด บอจ.5 ทำให้ธนาคารปลายทางไม่รับรู้โครงสร้างผู้ถือหุ้น",
      "แปลวัตถุประสงค์ไม่ครบทุกข้อ",
    ],
  }),
  "power-of-attorney": D({
    issuer: "ผู้มอบอำนาจจัดทำเอง แล้วรับรองลายมือชื่อโดยทนายผู้ทำคำรับรองลายมือชื่อฯ (NSA) หรือที่กรมการกงสุล",
    howToObtain: "จัดทำหนังสือมอบอำนาจระบุขอบเขตอำนาจให้ชัด แล้วลงนามต่อหน้าทนาย NSA พร้อมพยาน",
    prerequisites: [
      "ต้องลงนามต่อหน้าผู้รับรอง ห้ามลงนามมาก่อน",
      "ระบุชื่อผู้รับมอบอำนาจ เลขเอกสารแสดงตน และขอบเขตอำนาจอย่างเจาะจง",
      "แนบสำเนาบัตรประชาชน/พาสปอร์ตของทั้งสองฝ่าย",
    ],
    validity: "ปลายทางมักรับหนังสือมอบอำนาจที่ลงนามไม่เกิน 3-6 เดือน หรือระบุวันสิ้นสุดอำนาจไว้ในตัวเอกสาร",
    uses: ["ขายทรัพย์สินแทน", "ดำเนินคดีแทน", "ธุรกรรมธนาคาร", "จดทะเบียนบริษัท"],
    translationNote: "ต้องแปลขอบเขตอำนาจแบบคำต่อคำ การสรุปย่อทำให้ปลายทางตีความอำนาจแคบลงและปฏิเสธธุรกรรม",
    pitfalls: [
      "เขียนขอบเขตอำนาจกว้างลอย ๆ จนธนาคารหรือสำนักงานที่ดินปลายทางไม่รับ",
      "ลงนามล่วงหน้าแล้วนำไปให้ทนายรับรองภายหลัง",
      "ไม่ระบุวันที่และสถานที่ลงนาม",
    ],
  }),
  "court-judgment": D({
    issuer: "ศาลที่มีคำพิพากษา (ศาลแพ่ง ศาลเยาวชนและครอบครัว ฯลฯ)",
    howToObtain: "ยื่นคำร้องขอคัดสำเนาคำพิพากษาพร้อมหนังสือรับรองคดีถึงที่สุดจากงานคดีของศาล",
    prerequisites: [
      "ต้องมีหนังสือรับรองคดีถึงที่สุด มิฉะนั้นปลายทางถือว่าคดียังไม่สิ้นสุด",
      "สำเนาต้องมีตราศาลและลายมือชื่อเจ้าหน้าที่ผู้รับรอง",
      "คำแปลควรทำโดยนักแปลที่ขึ้นทะเบียนกระทรวงยุติธรรมเมื่อจะใช้ในกระบวนการศาลปลายทาง",
    ],
    validity: "ไม่มีวันหมดอายุ แต่ปลายทางมักขอสำเนารับรองใหม่ภายใน 6 เดือน",
    uses: ["บังคับคดีในต่างประเทศ", "ยืนยันสถานะหย่า", "สิทธิปกครองบุตร", "คดีมรดก"],
    translationNote: "ต้องแปลทั้งส่วนเหตุผลและส่วนคำพิพากษา รวมถึงเลขคดีและวันที่อ่านคำพิพากษา",
    pitfalls: [
      "แปลเฉพาะบทสรุปท้ายคำพิพากษา",
      "ไม่มีหนังสือรับรองคดีถึงที่สุดแนบ",
      "เลขคดีในคำแปลคลาดจากต้นฉบับแม้เพียงหลักเดียว",
    ],
  }),
  "medical-certificate": D({
    issuer: "โรงพยาบาลหรือคลินิกที่แพทย์มีใบอนุญาตประกอบวิชาชีพเวชกรรม",
    howToObtain: "ขอใบรับรองแพทย์ที่มีเลขที่ใบอนุญาตแพทย์ ตราโรงพยาบาล และวันที่ตรวจ",
    prerequisites: [
      "ต้องระบุเลขที่ใบอนุญาตประกอบวิชาชีพของแพทย์ผู้ลงนาม",
      "บางปลายทางกำหนดให้ออกจากโรงพยาบาลที่อยู่ในรายชื่อที่รับรอง",
      "กรมการกงสุลรับรองได้เมื่อมีลายมือชื่อที่ตรวจสอบได้",
    ],
    validity: "อายุสั้นที่สุดในกลุ่มเอกสาร โดยทั่วไป 1-3 เดือน",
    uses: ["วีซ่า", "ขึ้นทะเบียนวิชาชีพ", "สมัครงาน", "ประกันสุขภาพ"],
    translationNote: "ต้องแปลผลตรวจและชื่อโรคตามศัพท์การแพทย์สากล ไม่ควรแปลตามความเข้าใจทั่วไป",
    pitfalls: [
      "ใบรับรองไม่มีเลขที่ใบอนุญาตแพทย์",
      "เอกสารหมดอายุก่อนถึงวันยื่นเพราะอายุสั้น",
      "ใช้โรงพยาบาลที่ไม่อยู่ในรายชื่อที่ปลายทางกำหนด",
    ],
  }),
  "death-certificate": D({
    issuer: "สำนักงานเขต/ที่ว่าการอำเภอที่แจ้งตาย",
    howToObtain: "คัดมรณบัตร (ท.ร.4) ฉบับรับรองจากสำนักทะเบียน",
    prerequisites: [
      "ผู้ขอคัดต้องเป็นทายาทหรือผู้มีส่วนได้เสียพร้อมหลักฐานความสัมพันธ์",
      "ต้องมีตราประทับและลายมือชื่อนายทะเบียน",
      "คดีมรดกมักต้องใช้ทะเบียนบ้านที่จำหน่ายชื่อผู้ตายประกอบ",
    ],
    validity: "ปลายทางมักขอฉบับคัดใหม่ภายใน 6 เดือน",
    uses: ["คดีมรดกในต่างประเทศ", "เคลมประกันชีวิต", "ปรับสถานะคู่สมรส", "ปิดบัญชีธนาคาร"],
    translationNote: "ต้องแปลสาเหตุการตายตามข้อความในต้นฉบับ และคงเลขประจำตัวประชาชนของผู้ตายให้ครบ",
    pitfalls: [
      "แปลสาเหตุการตายแบบสรุปย่อ",
      "ไม่แนบเอกสารแสดงความสัมพันธ์ของผู้ยื่น",
      "ใช้สำเนาที่ไม่มีตรารับรองจากสำนักทะเบียน",
    ],
  }),
  "financial-statement": D({
    issuer: "บริษัทผู้จัดทำ ตรวจสอบโดยผู้สอบบัญชีรับอนุญาต (CPA) และนำส่ง DBD",
    howToObtain: "ขอคัดงบการเงินที่นำส่ง DBD พร้อมหน้ารายงานผู้สอบบัญชีและใบรับรองการนำส่ง",
    prerequisites: [
      "ต้องมีลายมือชื่อผู้สอบบัญชีพร้อมเลขทะเบียน CPA",
      "ควรแนบหนังสือรับรองบริษัทฉบับล่าสุดประกอบ",
      "งบต้องเป็นปีบัญชีล่าสุดที่นำส่งแล้ว",
    ],
    validity: "ปลายทางเชิงธุรกิจมักรับงบของปีบัญชีล่าสุดและไม่เกิน 12 เดือนนับจากวันนำส่ง",
    uses: ["ขอสินเชื่อต่างประเทศ", "ประมูลงาน", "ตั้งบริษัทลูก", "ยื่นต่อนักลงทุน"],
    translationNote: "ต้องแปลหมายเหตุประกอบงบการเงินด้วย เพราะธนาคารปลายทางใช้ส่วนนี้ประเมินภาระผูกพัน",
    pitfalls: [
      "แปลเฉพาะงบดุลและงบกำไรขาดทุน ตัดหมายเหตุประกอบออก",
      "ใช้งบที่ยังไม่ได้นำส่ง DBD",
      "ตัวเลขในคำแปลใช้เครื่องหมายคั่นหลักคนละแบบจนอ่านผิดหลัก",
    ],
  }),
};

/** Cross-cutting rejection causes used as a fallback when no entity-specific list exists. */
export const GENERIC_PITFALLS: string[] = [
  "ชื่อ-สกุลในคำแปลสะกดไม่ตรงกับหนังสือเดินทาง (สาเหตุอันดับหนึ่ง)",
  "ใช้สำเนาถ่ายเอกสารแทนฉบับคัดรับรองที่มีลายมือชื่อเจ้าหน้าที่",
  "ทำ Apostille เฉพาะต้นฉบับ แต่ลืมรับรองคำแปลไปพร้อมกัน",
  "แกะแม็กซ์หรือเย็บชุดเอกสารใหม่ ทำให้ตราประทับคาบรอยต่อขาดจากกัน",
];
